import { isCapacitorPlatform } from "../helpers";
import { StorageStateKeysEnum } from "@utils/storage";
import { PushNotificationsTokenApiService } from "@api-services/push-notifications/PushNotificationsTokenApiService";
import { DateTime } from "luxon";
import { getCurrentCulture } from "@utils/common/culture";
import { isGamesTheme } from "@utils/common/theme";
import { redirect } from "@state";
import {
	createSearchQueryForEndEventFromResult,
	isFullTimeScoreInResult,
	isValidBetSlipPayload,
	isValidEndEventPayload,
	isValidEventPayload,
} from "./pushNotificationHelpers";
import {
	MatchStatusAbrv,
	NotificationEventType,
	Payload,
} from "./pushNotificationTypes";
import {
	ActionPerformed,
	PushNotifications,
	Token,
} from "@capacitor/push-notifications";

/**
 * Push notifications handler for Hybrid apps
 * Its functions should be called only in case for Hybrid apps
 */
export class PushNotificationsHandler {
	static async initialize() {
		if (!isCapacitorPlatform() || isGamesTheme()) {
			return;
		}

		// dispose all notification listeners and start them again later
		// we dont have a way to track if listeners are already attached
		this.disposeListeners();

		const shouldEnablePushNotifications = await this.checkPermissions();

		if (!shouldEnablePushNotifications) {
			return;
		}

		this.startListeners();
		this.registerDevice();
	}

	static async checkPermissions() {
		const permissionStatus = await this.getPermissionStatus();

		if (permissionStatus === "granted") {
			return true;
		}

		if (permissionStatus === "prompt") {
			const permissionsGiven = await this.askForPermissions();
			return permissionsGiven;
		}

		//status not prompt or granted
		return false;
	}

	/**
	 * Checks push notifications permissions
	 */
	static async getPermissionStatus(): Promise<
		"prompt" | "prompt-with-rationale" | "granted" | "denied"
	> {
		const permStatus = await PushNotifications.checkPermissions();
		return permStatus.receive;
	}

	/**
	 * Prompts user to allow push notifications
	 */
	static async askForPermissions() {
		const permStatus = await PushNotifications.requestPermissions();
		return permStatus.receive === "granted";
	}

	/**
	 * Register the app to receive push notifications.
	 * Triggers registration event or registration error event
	 */
	static async registerDevice() {
		await PushNotifications.register();
	}

	/**
	 * Unregister the app from push notifications.
	 * This will delete a firebase token on Android, and unregister APNS on iOS.
	 */
	static unregisterDevice() {
		PushNotifications.unregister();
	}

	static getTokenFromStorage() {
		return JSON.parse(
			App.state.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.HYBRID_PN_TOKEN
			) as string
		) as {
			token: string;
			shouldSendToBE: boolean;
			initEntryExistsOnBE: boolean;
			refreshToken?: string;
		} | null;
	}

	static setTokenToStorage(tokenInfo: {
		token: string;
		shouldSendToBE: boolean;
		initEntryExistsOnBE: boolean;
		refreshToken?: string;
	}) {
		App.state.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.HYBRID_PN_TOKEN,
			JSON.stringify(tokenInfo)
		);
	}

	static syncTokenWithLocalStorage(newToken: Token) {
		const currentToken = this.getTokenFromStorage();

		if (!currentToken) {
			this.setTokenToStorage({
				token: newToken.value,
				shouldSendToBE: true,
				initEntryExistsOnBE: false,
			});
			this.updateUserToken();
			return;
		}

		if (
			currentToken.token !== newToken.value &&
			currentToken.initEntryExistsOnBE
		) {
			this.setTokenToStorage({
				token: currentToken.token,
				shouldSendToBE: true,
				initEntryExistsOnBE: true,
				refreshToken: newToken.value,
			});
			this.updateUserToken();
			return;
		}

		if (currentToken.token !== newToken.value) {
			this.setTokenToStorage({
				token: newToken.value,
				shouldSendToBE: true,
				initEntryExistsOnBE: false,
			});
			this.updateUserToken();
			return;
		}
	}

	static async updateUserToken() {
		if (
			!isCapacitorPlatform() ||
			!App.state.rootStore.userAuthStore.isLoggedIn ||
			isGamesTheme()
		) {
			return;
		}

		const permStatus = await this.getPermissionStatus();

		if (permStatus != "granted") {
			return;
		}

		const currentTokenValues = this.getTokenFromStorage();

		if (!currentTokenValues?.shouldSendToBE || !currentTokenValues?.token) {
			return;
		}

		if (
			currentTokenValues.refreshToken &&
			currentTokenValues.initEntryExistsOnBE
		) {
			try {
				await PushNotificationsTokenApiService.updateUserPushNotificationToken(
					currentTokenValues.token,
					currentTokenValues.refreshToken,
					DateTime.now().toISO()
				);

				this.setTokenToStorage({
					token: currentTokenValues.refreshToken,
					shouldSendToBE: false,
					initEntryExistsOnBE: true,
				});
			} catch (error) {
				console.error(error);
			}
			return;
		}

		try {
			await PushNotificationsTokenApiService.setUserPushNotificationToken(
				currentTokenValues.token
			);

			this.setTokenToStorage({
				token: currentTokenValues.token,
				shouldSendToBE: false,
				initEntryExistsOnBE: true,
			});
		} catch (error) {
			console.error(error);
		}

		App.state.rootStore.notificationSettingsStore.onTokenChange();
	}

	static startListeners() {
		this.registrationTokenListener();
		this.registrationTokenErrorListener();
		this.pushNotificationReceivedListener();
		this.pushNotificationActionListener();
	}

	static registrationTokenListener() {
		PushNotifications.addListener("registration", (token) => {
			console.log(token.value);
			this.syncTokenWithLocalStorage(token);
		});
	}

	static registrationTokenErrorListener() {
		PushNotifications.addListener("registrationError", (error) => {
			console.error(error);
		});
	}

	static pushNotificationReceivedListener() {
		PushNotifications.addListener(
			"pushNotificationReceived",
			(notifications) => {
				console.log("Notifications received", notifications);
			}
		);
	}

	static pushNotificationActionListener() {
		PushNotifications.addListener(
			"pushNotificationActionPerformed",
			(notification) => {
				console.log("Notification action on", notification);
				this.handleNotificationAction(notification);
			}
		);
	}

	static disposeListeners() {
		PushNotifications.removeAllListeners();
	}

	//notification action handlers

	static handleNotificationAction(notification: ActionPerformed) {
		const notificationType = notification.notification.data.type;
		const notificationPayload = notification.notification.data.payload;

		switch (notificationType) {
			case NotificationEventType.BET_SLIP:
				this.handleBetSlipTypeNotification(notificationPayload);
				break;
			case NotificationEventType.EVENT:
				this.handleEventTypeNotification(notificationPayload);
				break;
		}
	}

	static handlePayloadParsing(payload: string | null): Payload | null {
		if (!payload) {
			console.error("payload null, should be string");
			return null;
		}

		const parsedPayload = JSON.parse(payload) as Payload | null;

		return parsedPayload;
	}

	static handleBetSlipTypeNotification(payload: string | null) {
		const parsedPayload = this.handlePayloadParsing(payload);

		if (!parsedPayload || !isValidBetSlipPayload(parsedPayload)) {
			return;
		}

		const userStore = App.state.rootStore.userAuthStore;

		const userIdForDetails = parsedPayload.userId;

		if (!userStore.isLoggedIn) {
			redirect(
				`/${getCurrentCulture()}/auth/login?returnUrl=/my-bets/details/${
					parsedPayload.betSlipId
				}/${userIdForDetails}`
			);
			return;
		}

		redirect(
			`/${getCurrentCulture()}/my-bets/details/${
				parsedPayload.betSlipId
			}/${userIdForDetails}`
		);
	}

	static handleEventTypeNotification(payload: string | null) {
		const parsedPayload = this.handlePayloadParsing(payload);

		if (!parsedPayload || !isValidEventPayload(parsedPayload) || !payload) {
			return;
		}

		const matchStatus = parsedPayload.matchStatusAbrv;

		if (
			matchStatus === MatchStatusAbrv.START ||
			matchStatus === MatchStatusAbrv.HT
		) {
			//redirect user to event page
			//check if events with start or halftime status are always available on live/events/event/eventid
			redirect(
				`/${getCurrentCulture()}/live/matches/event/${
					parsedPayload.eventId
				}`
			);
		} else if (
			matchStatus === MatchStatusAbrv.END &&
			isValidEndEventPayload(parsedPayload) &&
			isFullTimeScoreInResult(parsedPayload)
		) {
			//redirect user to custom event page
			//use payload data to show custom event page when event not in offer anymore
			const searchQuery = createSearchQueryForEndEventFromResult(payload);

			redirect(
				`/${getCurrentCulture()}/event-summary?${searchQuery.toString()}`
			);
		}
	}
}
